import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';

import { HDIinfoIcon } from 'icons';
import { amplitudeService } from 'services';
import { LocalTzTime } from '../LocalTzTime';
import { countriesLinks, HDIRatingLabels, instructions } from '../consts';
import { AugmentedTimeZoneWithCountryData } from '../types';

export const ResultsTable = ({
  data,
}: {
  data: AugmentedTimeZoneWithCountryData[];
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer
        sx={{
          px: '6px',
        }}
      >
        <Table
          sx={{
            // minWidth: 'max-content',
            minWidth: '880px',
            borderCollapse: 'separate',
            borderSpacing: '0 8px',
            background: 'transparent',
            '.MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow
              sx={{
                background: (theme) => theme.palette.remoteLevelCard.bg,
                borderRadius: '6px',
                '.MuiTableCell-root': {
                  fontWeight: 500,
                  padding: '6px 24px',
                  color: (theme) => theme.palette.primary.main,
                },

                '.MuiTableCell-root:first-of-type': {
                  borderBottomLeftRadius: '6px',
                  borderTopLeftRadius: '6px',
                },

                '.MuiTableCell-root:last-of-type': {
                  borderBottomRightRadius: '6px',
                  borderTopRightRadius: '6px',
                },
              }}
            >
              <TableCell>Country</TableCell>
              <TableCell align="center">Working hours overlap</TableCell>
              <TableCell align="center">
                <Tooltip title={instructions} placement="top">
                  <Box>
                    <Box display="inline-block" paddingRight="4px">
                      <HDIinfoIcon />
                    </Box>
                    Human Development Index
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '&:before': {
                display: 'block',
                lineHeight: '4px',
                content: "'-'",
                opacity: 0,
              },
            }}
          >
            {data.map((row) => (
              <TableRow
                key={row.tzNameLabel}
                sx={{
                  boxShadow: '0px 2px 10px -1px rgba(48, 78, 118, 0.08)',
                  background: 'transparent',
                  borderRadius: '10px',

                  '.MuiTableCell-root': {
                    fontWeight: 600,
                    padding: '24px',
                    background: (theme) => theme.palette.grey.A100,
                    verticalAlign: 'top',
                  },
                  '.MuiTableCell-root:first-of-type': {
                    borderBottomLeftRadius: '10px',
                    borderTopLeftRadius: '10px',
                  },

                  '.MuiTableCell-root:last-of-type': {
                    borderBottomRightRadius: '10px',
                    borderTopRightRadius: '10px',
                  },
                }}
              >
                <TableCell
                  scope="row"
                  sx={{
                    width: '200px',
                    maxWidth: '200px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '20px',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '46px',
                        filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15))',
                        flexShrink: 0,
                      }}
                      className={`fi fi-${row.country.id.toLowerCase()}`}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography fontWeight={600} variant="textMedium">
                        {row.country.name} {row.hiringGuidesData.url && <></>}
                      </Typography>
                      <Typography color="grey.300" variant="textSmall">
                        {row.tzNameLabel}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                  }}
                  align="center"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography fontWeight={600} variant="textMedium">
                      {row.overlapInHours} hours overlap
                    </Typography>

                    <LocalTzTime offset={row.tzData.offset()} />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ width: '150px', fontWeight: 600 }}
                  align="center"
                >
                  <Typography fontWeight={600} variant="textMedium">
                    {HDIRatingLabels[row.HDIData?.rating ?? ''] ?? 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: '100px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Button
                      sx={{
                        textAlign: 'center',
                      }}
                      variant="contained"
                      href={row.costCalcData.url}
                      target="_blank"
                    >
                      Calculate cost
                    </Button>
                    {row.hiringGuidesData.url && (
                      <Button
                        onClick={() => {
                          const mappedCountryName =
                            countriesLinks[row.country.name];
                          mappedCountryName &&
                            amplitudeService.logHiringGuidesClick(
                              mappedCountryName
                            );
                        }}
                        href={row.hiringGuidesData.url}
                        target="_blank"
                        variant="outlined"
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        Hiring guide
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
